import React from "react";
import {
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
} from "react-native";
import { useTheme } from "react-native-paper";

import type { ExtendedTheme } from "../../theme/SynapseTheme";
import ModalCloseButton from "../ModalCloseButton";

interface ModalHeaderProps {
  title: React.ReactNode;
  onClose?: () => void;
  /** Optional element before the title (e.g. an icon). */
  leading?: React.ReactNode;
  titleColor?: string;
  titleStyle?: StyleProp<TextStyle>;
  testID?: string;
}

/**
 * The one header every modal and dialog uses: a left-aligned 20/700 title with
 * the close button centred on the same row, over an outlineVariant hairline.
 */
const ModalHeader: React.FC<ModalHeaderProps> = ({
  title,
  onClose,
  leading,
  titleColor,
  titleStyle,
  testID,
}) => {
  const { colors } = useTheme() as ExtendedTheme;
  return (
    <View
      style={[styles.row, { borderBottomColor: colors.outlineVariant }]}
      testID={testID}
    >
      {leading ? <View style={styles.leading}>{leading}</View> : null}
      <Text
        accessibilityRole="header"
        numberOfLines={2}
        style={[
          styles.title,
          { color: titleColor ?? colors.onSurface },
          titleStyle,
        ]}
      >
        {title}
      </Text>
      {onClose ? <ModalCloseButton onPress={onClose} /> : null}
    </View>
  );
};

const styles = StyleSheet.create({
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingLeft: 20,
    paddingRight: 12,
    paddingVertical: 12,
    minHeight: 56,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  leading: {
    marginRight: 10,
  },
  title: {
    flex: 1,
    fontSize: 20,
    lineHeight: 26,
    fontWeight: "700",
  },
});

export default ModalHeader;
